import React from 'react';
import { Link } from 'react-router-dom';
import {
  AdminUserSummaryDto,
  AdminUserActivityFilter,
  AdminUserSortBy,
  Auth0UserSyncResponse,
  BooleanFilterValue,
  exportAdminUsersCsv,
  getAdminUsers,
  getAdminNotificationCommunities,
  AdminNotificationCommunityOption,
  PageDto,
  StripeConnectAccountStatus,
  syncAuth0Users,
  useApiClient,
} from '../api/client';
import type { AdminUserFilterQuery } from '../api/client';
import { ActionMenu, PageHeader, PageShell, PaginationBar, StatusBadge } from '../components/ui';
import { useI18n } from '../i18n/I18nProvider';
import { useToast } from '../toast/ToastProvider';
import { formatDateTime } from '../utils/dateFormat';

const DEFAULT_PAGE_SIZE = 10;
const ACTIVITY_FILTERS: AdminUserActivityFilter[] = ['any', '24h', '7d', '30d', '90d', 'never'];
const SORT_OPTIONS: AdminUserSortBy[] = ['LAST_SEEN_AT', 'CREATED_AT', 'UPDATED_AT', 'EMAIL'];
const STRIPE_STATUSES: StripeConnectAccountStatus[] = ['NO_ACCOUNT', 'ONBOARDING_REQUIRED', 'IN_REVIEW', 'READY', 'PAYOUTS_DISABLED'];

function formatName(user: AdminUserSummaryDto, fallback: string) {
  return [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email || fallback;
}

function formatMaybeDate(value?: string | null) {
  return value ? formatDateTime(value) : '—';
}

function UserAvatar({ name, url }: { name: string; url?: string | null }) {
  const [failed, setFailed] = React.useState(false);
  return url && !failed
    ? <img className="admin-avatar" src={url} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />
    : <span className="admin-avatar" aria-hidden="true">{name.slice(0, 1).toUpperCase()}</span>;
}

function shortId(value: string) {
  if (value.length <= 18) return value;
  const separatorIndex = value.indexOf('|');
  if (separatorIndex > 0 && separatorIndex < 12) {
    return `${value.slice(0, separatorIndex + 1)}${value.slice(separatorIndex + 1, separatorIndex + 8)}…`;
  }
  return `${value.slice(0, 15)}…`;
}

function activitySinceIso(filter: AdminUserActivityFilter) {
  const days = { '24h': 1, '7d': 7, '30d': 30, '90d': 90 };
  return filter in days ? new Date(Date.now() - days[filter as keyof typeof days] * 86400000).toISOString() : null;
}

function TriStateFilter({ label, value, onChange }: {
  label: string;
  value: BooleanFilterValue;
  onChange: (value: BooleanFilterValue) => void;
}) {
  const { t } = useI18n();
  return (
    <label className="admin-filter-field">
      <span>{label}</span>
      <select className="form-control" value={value} onChange={(event) => onChange(event.target.value as BooleanFilterValue)}>
        <option value="any">{t('users.any')}</option>
        <option value="true">{t('users.yes')}</option>
        <option value="false">{t('users.no')}</option>
      </select>
    </label>
  );
}

function OnboardingBadge({ label, value }: { label: string; value: boolean }) {
  const { t } = useI18n();
  return <StatusBadge tone={value ? 'green' : 'slate'}>{label}: {t(value ? 'users.yes' : 'users.no')}</StatusBadge>;
}

export const AdminUsersPage: React.FC = () => {
  const { request, authFetch } = useApiClient();
  const { showError, showSuccess } = useToast();
  const { t } = useI18n();
  const [page, setPage] = React.useState<PageDto<AdminUserSummaryDto> | null>(null);
  const [q, setQ] = React.useState('');
  const [includeDeleted, setIncludeDeleted] = React.useState(false);
  const [activityFilter, setActivityFilter] = React.useState<AdminUserActivityFilter>('any');
  const [agbAccepted, setAgbAccepted] = React.useState<BooleanFilterValue>('any');
  const [hasBio, setHasBio] = React.useState<BooleanFilterValue>('any');
  const [diditVerified, setDiditVerified] = React.useState<BooleanFilterValue>('any');
  const [hasVehicle, setHasVehicle] = React.useState<BooleanFilterValue>('any');
  const [stripeConnectStatus, setStripeConnectStatus] = React.useState<StripeConnectAccountStatus | 'any'>('any');
  const [hasCreatedRides, setHasCreatedRides] = React.useState<BooleanFilterValue>('any');
  const [hasCreatedRideRequests, setHasCreatedRideRequests] = React.useState<BooleanFilterValue>('any');
  const [hasCompletedRides, setHasCompletedRides] = React.useState<BooleanFilterValue>('any');
  const [hasCompletedRideRequests, setHasCompletedRideRequests] = React.useState<BooleanFilterValue>('any');
  const [communityId, setCommunityId] = React.useState('');
  const [communities, setCommunities] = React.useState<AdminNotificationCommunityOption[]>([]);
  const [groupId, setGroupId] = React.useState('');
  const [emailOptedOut, setEmailOptedOut] = React.useState<BooleanFilterValue>('any');
  const [hasSearches, setHasSearches] = React.useState<BooleanFilterValue>('any');
  const [hasEmergencyContacts, setHasEmergencyContacts] = React.useState<BooleanFilterValue>('any');
  const [hasReceivedReviews, setHasReceivedReviews] = React.useState<BooleanFilterValue>('any');
  const [hasGivenReviews, setHasGivenReviews] = React.useState<BooleanFilterValue>('any');
  const [minAverageRating, setMinAverageRating] = React.useState('');
  const [sortBy, setSortBy] = React.useState<AdminUserSortBy>('LAST_SEEN_AT');
  const [sortDirection, setSortDirection] = React.useState<'ASC' | 'DESC'>('DESC');
  const [showFilters, setShowFilters] = React.useState(false);
  const [pageNumber, setPageNumber] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [syncingAuth0, setSyncingAuth0] = React.useState(false);
  const [auth0SyncResult, setAuth0SyncResult] = React.useState<Auth0UserSyncResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    getAdminNotificationCommunities(request)
      .then((result) => { if (!cancelled) setCommunities(result); })
      .catch((err) => { if (!cancelled) showError((err as Error).message || t('users.communitiesFailed')); });
    return () => { cancelled = true; };
  }, [request, showError, t]);

  const userFilters = React.useMemo<AdminUserFilterQuery>(() => ({
    q,
    includeDeleted,
    lastSeenSince: activitySinceIso(activityFilter),
    neverSeen: activityFilter === 'never' ? true : undefined,
    agbAccepted,
    hasBio,
    diditVerified,
    hasVehicle,
    stripeConnectStatus,
    hasCreatedRides,
    hasCreatedRideRequests,
    hasCompletedRides,
    hasCompletedRideRequests,
    communityId,
    groupId,
    hasSearches,
    emailOptedOut,
    hasEmergencyContacts,
    hasReceivedReviews,
    hasGivenReviews,
    minAverageRating: minAverageRating ? Number(minAverageRating) : null,
    sortBy,
    sortDirection,
  }), [
    agbAccepted,
    activityFilter,
    diditVerified,
    hasBio,
    hasCompletedRideRequests,
    hasCompletedRides,
    communityId,
    groupId,
    hasSearches,
    emailOptedOut,
    hasEmergencyContacts,
    hasReceivedReviews,
    hasGivenReviews,
    minAverageRating,
    hasCreatedRideRequests,
    hasCreatedRides,
    hasVehicle,
    includeDeleted,
    q,
    sortBy,
    sortDirection,
    stripeConnectStatus,
  ]);

  const requestGeneration = React.useRef(0);
  const invalidateRequests = React.useCallback(() => {
    ++requestGeneration.current;
  }, []);
  const load = React.useCallback(async () => {
    const generation = ++requestGeneration.current;
    setLoading(true);
    setError(null);
    try {
      const result = await getAdminUsers(request, { page: pageNumber, size: pageSize, ...userFilters });
      if (generation === requestGeneration.current) setPage(result);
    } catch (err) {
      if (generation !== requestGeneration.current) return;
      const message = (err as Error)?.message || t('users.loadFailed');
      setError(message);
      showError(message);
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  }, [pageNumber, pageSize, request, showError, t, userFilters]);

  const latestLoad = React.useRef(load);
  latestLoad.current = load;

  React.useEffect(() => {
    // Invalidate in-flight results immediately, including during the search debounce.
    invalidateRequests();
    setLoading(true);
    setError(null);
    const timeout = window.setTimeout(load, 250);
    return () => {
      window.clearTimeout(timeout);
      invalidateRequests();
    };
  }, [invalidateRequests, load]);

  const exportCsv = React.useCallback(async () => {
    setExporting(true);
    try {
      const exportFile = await exportAdminUsersCsv(authFetch, userFilters);
      const url = window.URL.createObjectURL(exportFile.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportFile.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showError((err as Error)?.message || t('users.exportFailed'));
    } finally {
      setExporting(false);
    }
  }, [authFetch, showError, t, userFilters]);

  const syncUsersFromAuth0 = React.useCallback(async () => {
    setSyncingAuth0(true);
    setAuth0SyncResult(null);
    try {
      const result = await syncAuth0Users(request, false);
      setAuth0SyncResult(result);
      showSuccess(t('users.syncSuccess', { count: result.created }));
      await latestLoad.current();
    } catch (err) {
      showError((err as Error)?.message || t('users.syncFailed'));
    } finally {
      setSyncingAuth0(false);
    }
  }, [request, showError, showSuccess, t]);

  const clearFilters = () => {
    setQ('');
    setIncludeDeleted(false);
    setActivityFilter('any');
    setAgbAccepted('any');
    setHasBio('any');
    setDiditVerified('any');
    setHasVehicle('any');
    setStripeConnectStatus('any');
    setHasCreatedRides('any');
    setHasCreatedRideRequests('any');
    setHasCompletedRides('any');
    setHasCompletedRideRequests('any');
    setCommunityId('');
    setGroupId('');
    setHasSearches('any');
    setEmailOptedOut('any');
    setHasEmergencyContacts('any');
    setHasReceivedReviews('any');
    setHasGivenReviews('any');
    setMinAverageRating('');
    setPageNumber(0);
  };

  const booleanFilters = [
    { key: 'agbAccepted', value: agbAccepted, set: setAgbAccepted, group: 'profile' },
    { key: 'hasBio', value: hasBio, set: setHasBio, group: 'profile' },
    { key: 'diditVerified', value: diditVerified, set: setDiditVerified, group: 'profile' },
    { key: 'hasVehicle', value: hasVehicle, set: setHasVehicle, group: 'profile' },
    { key: 'hasCreatedRides', value: hasCreatedRides, set: setHasCreatedRides, group: 'rides' },
    { key: 'hasCreatedRideRequests', value: hasCreatedRideRequests, set: setHasCreatedRideRequests, group: 'rides' },
    { key: 'hasCompletedRides', value: hasCompletedRides, set: setHasCompletedRides, group: 'rides' },
    { key: 'hasCompletedRideRequests', value: hasCompletedRideRequests, set: setHasCompletedRideRequests, group: 'rides' },
    { key: 'hasSearches', value: hasSearches, set: setHasSearches, group: 'rides' },
    { key: 'emailOptedOut', value: emailOptedOut, set: setEmailOptedOut, group: 'community' },
    { key: 'hasEmergencyContacts', value: hasEmergencyContacts, set: setHasEmergencyContacts, group: 'reviews' },
    { key: 'hasReceivedReviews', value: hasReceivedReviews, set: setHasReceivedReviews, group: 'reviews' },
    { key: 'hasGivenReviews', value: hasGivenReviews, set: setHasGivenReviews, group: 'reviews' },
  ];
  const activeFilters: Array<{ key: string; label: string; clear: () => void }> = booleanFilters
    .filter((filter) => filter.value !== 'any')
    .map((filter) => ({
      key: filter.key,
      label: `${t(`users.filter.${filter.key}`)}: ${t(filter.value === 'true' ? 'users.yes' : 'users.no')}`,
      clear: () => filter.set('any'),
    }));
  if (q) activeFilters.push({ key: 'search', label: `${t('users.search')}: ${q}`, clear: () => setQ('') });
  if (includeDeleted) activeFilters.push({ key: 'deleted', label: t('users.includeDeleted'), clear: () => setIncludeDeleted(false) });
  if (activityFilter !== 'any') activeFilters.push({ key: 'activity', label: t(`users.activity.${activityFilter}`), clear: () => setActivityFilter('any') });
  if (stripeConnectStatus !== 'any') activeFilters.push({ key: 'stripe', label: `${t('users.filter.stripe')}: ${t(`users.stripe.${stripeConnectStatus}`)}`, clear: () => setStripeConnectStatus('any') });
  if (communityId) activeFilters.push({ key: 'community', label: `${t('users.filter.community')}: ${communities.find((c) => c.id === communityId)?.name || t('users.filter.unavailableCommunity')}`, clear: () => setCommunityId('') });
  if (groupId) activeFilters.push({ key: 'group', label: `${t('users.filter.group')}: ${groupId}`, clear: () => setGroupId('') });
  if (minAverageRating) activeFilters.push({ key: 'rating', label: `${t('users.filter.rating')}: ≥ ${minAverageRating}`, clear: () => setMinAverageRating('') });

  const rows = page?.content || [];
  const renderBooleanFilters = (group: string) => booleanFilters.filter((filter) => filter.group === group).map((filter) => (
    <TriStateFilter key={filter.key} label={t(`users.filter.${filter.key}`)} value={filter.value}
      onChange={(value) => { filter.set(value); setPageNumber(0); }} />
  ));

  return (
    <PageShell>
      <PageHeader title={t('users.title')} description={t('users.description')} />
      <section className="admin-panel admin-user-controls" aria-label={t('users.search')}>
        <div className="admin-user-toolbar">
          <input className="form-control admin-search" type="search" aria-label={t('users.search')}
            placeholder={t('users.searchPlaceholder')} value={q}
            onChange={(event) => { setQ(event.target.value); setPageNumber(0); }} />
          <button className="btn btn-neutral" type="button" aria-expanded={showFilters} aria-controls="admin-user-filters"
            onClick={() => setShowFilters((value) => !value)}>
            {t('users.filters')}{activeFilters.length > 0 ? ` (${activeFilters.length})` : ''}
          </button>
          <button className="btn btn-neutral" type="button" disabled={loading} onClick={load}>{t('users.refresh')}</button>
          <ActionMenu label={t('users.actions')}>
            <button type="button" disabled={exporting} onClick={exportCsv}>{t(exporting ? 'users.exporting' : 'users.export')}</button>
            <button type="button" disabled={syncingAuth0} onClick={syncUsersFromAuth0}>{t(syncingAuth0 ? 'users.syncing' : 'users.sync')}</button>
          </ActionMenu>
        </div>
        <div className="admin-user-secondary-toolbar">
          <div className="admin-user-quick-filters">
            <button className="btn btn-neutral" type="button" aria-pressed={activityFilter === '7d'}
              onClick={() => { setActivityFilter(activityFilter === '7d' ? 'any' : '7d'); setPageNumber(0); }}>
              {t('users.quick.recent')}
            </button>
            <button className="btn btn-neutral" type="button" aria-pressed={diditVerified === 'true'}
              onClick={() => { setDiditVerified(diditVerified === 'true' ? 'any' : 'true'); setPageNumber(0); }}>
              {t('users.quick.didit')}
            </button>
          </div>
          <div className="admin-user-sort-controls">
            <label className="admin-filter-field"><span>{t('users.sort')}</span>
              <select className="form-control" value={sortBy} onChange={(event) => { setSortBy(event.target.value as AdminUserSortBy); setPageNumber(0); }}>
                {SORT_OPTIONS.map((value) => <option key={value} value={value}>{t(`users.sort.${value}`)}</option>)}
              </select>
            </label>
            <label className="admin-filter-field"><span>{t('users.direction')}</span>
              <select className="form-control" value={sortDirection} onChange={(event) => { setSortDirection(event.target.value as 'ASC' | 'DESC'); setPageNumber(0); }}>
                <option value="DESC">{t('users.direction.DESC')}</option><option value="ASC">{t('users.direction.ASC')}</option>
              </select>
            </label>
            <label className="admin-filter-field"><span>{t('users.pageSize')}</span>
              <select className="form-control" value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPageNumber(0); }}>
                {[10, 20, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
            </label>
          </div>
        </div>
        {activeFilters.length > 0 && (
          <div className="admin-user-active-filters" aria-label={t('users.activeFilters')}>
            {activeFilters.map((filter) => (
              <button key={filter.key} className="admin-filter-chip" type="button"
                aria-label={t('users.removeFilter', { filter: filter.label })}
                onClick={() => { filter.clear(); setPageNumber(0); }}>
                {filter.label}<span aria-hidden="true">×</span>
              </button>
            ))}
            <button className="admin-text-button" type="button" onClick={clearFilters}>{t('users.clearFilters')}</button>
          </div>
        )}
        {showFilters && (
          <div id="admin-user-filters" className="admin-user-filter-panel" role="region" aria-label={t('users.advancedFilters')}>
            <fieldset className="admin-filter-section"><legend>{t('users.filters.profile')}</legend>
              <div className="admin-filter-grid">
                {renderBooleanFilters('profile')}
                <label className="admin-filter-field"><span>{t('users.filter.stripe')}</span>
                  <select className="form-control" value={stripeConnectStatus} onChange={(event) => { setStripeConnectStatus(event.target.value as StripeConnectAccountStatus | 'any'); setPageNumber(0); }}>
                    <option value="any">{t('users.any')}</option>
                    {STRIPE_STATUSES.map((value) => <option key={value} value={value}>{t(`users.stripe.${value}`)}</option>)}
                  </select>
                </label>
                <label className="admin-filter-checkbox"><input type="checkbox" checked={includeDeleted}
                  onChange={(event) => { setIncludeDeleted(event.target.checked); setPageNumber(0); }} />{t('users.includeDeleted')}</label>
              </div>
            </fieldset>
            <fieldset className="admin-filter-section"><legend>{t('users.filters.rides')}</legend>
              <div className="admin-filter-grid">
                <label className="admin-filter-field"><span>{t('users.filter.activity')}</span>
                  <select className="form-control" value={activityFilter} onChange={(event) => { setActivityFilter(event.target.value as AdminUserActivityFilter); setPageNumber(0); }}>
                    {ACTIVITY_FILTERS.map((value) => <option key={value} value={value}>{t(`users.activity.${value}`)}</option>)}
                  </select>
                </label>
                {renderBooleanFilters('rides')}
              </div>
            </fieldset>
            <fieldset className="admin-filter-section"><legend>{t('users.filters.community')}</legend>
              <div className="admin-filter-grid">
                <label className="admin-filter-field"><span>{t('users.filter.community')}</span>
                  <select className="form-control" value={communityId} onChange={(event) => { setCommunityId(event.target.value); setPageNumber(0); }}>
                    <option value="">{t('users.filter.allCommunities')}</option>
                    {communityId && !communities.some((community) => community.id === communityId) && <option value={communityId}>{t('users.filter.unavailableCommunity')}</option>}
                    {communities.map((community) => <option key={community.id} value={community.id}>{community.name}</option>)}
                  </select>
                </label>
                <label className="admin-filter-field"><span>{t('users.filter.group')}</span>
                  <input className="form-control" value={groupId} placeholder={t('users.filter.groupPlaceholder')} onChange={(event) => { setGroupId(event.target.value); setPageNumber(0); }} />
                </label>
                {renderBooleanFilters('community')}
              </div>
            </fieldset>
            <fieldset className="admin-filter-section"><legend>{t('users.filters.reviews')}</legend>
              <div className="admin-filter-grid">
                {renderBooleanFilters('reviews')}
                <label className="admin-filter-field"><span>{t('users.filter.rating')}</span>
                  <select className="form-control" value={minAverageRating} onChange={(event) => { setMinAverageRating(event.target.value); setPageNumber(0); }}>
                    <option value="">{t('users.any')}</option>
                    {[1, 2, 3, 4, 4.5, 5].map((rating) => <option key={rating} value={rating}>{rating}+</option>)}
                  </select>
                </label>
              </div>
            </fieldset>
          </div>
        )}
      </section>
      {auth0SyncResult && <div className="admin-user-sync-result" role="status">
        <strong>{t('users.synced', { count: auth0SyncResult.created })}</strong>{' '}
        {t('users.syncDetails', { checked: auth0SyncResult.auth0UsersChecked, existing: auth0SyncResult.alreadyExisting, skipped: auth0SyncResult.skipped })}
      </div>}
      {error && <div className="admin-user-error" role="alert">{error}</div>}
      <section className="admin-panel admin-user-directory" aria-label={t('users.title')} aria-busy={loading}>
        <div className="admin-user-table-meta">
          <span role="status">{loading ? t('users.loading') : t('users.found', { count: page?.totalElements || 0 })}</span>
          <span>{t('users.page', { page: (page?.number || 0) + 1, total: Math.max(page?.totalPages || 1, 1) })}</span>
        </div>
        <div className="admin-table-scroll" tabIndex={0} role="region" aria-label={t('users.title')}>
          <table className="admin-table admin-users-table">
            <thead><tr>
              <th scope="col">{t('users.table.person')}</th><th scope="col">{t('users.table.onboarding')}</th>
              <th scope="col">{t('users.table.verification')}</th><th scope="col">{t('users.table.activity')}</th>
              <th scope="col">{t('users.table.lastSeen')}</th>
            </tr></thead>
            <tbody>
              {rows.map((user) => {
                const name = formatName(user, t('users.unknown'));
                const verified = user.verificationStatus === 'VERIFIED' || user.verificationStatus === 'VERIFIED_DRIVER';
                const verificationKey = ['VERIFIED', 'VERIFIED_DRIVER', 'NOT_VERIFIED'].includes(user.verificationStatus || 'NOT_VERIFIED')
                  ? user.verificationStatus || 'NOT_VERIFIED' : 'OTHER';
                return <tr key={user.id} className={user.deletedAt ? 'admin-user-deleted' : undefined}>
                  <td>
                    <div className="admin-user-identity">
                      <UserAvatar key={user.profilePictureUrl} name={name} url={user.profilePictureUrl} />
                      <div className="admin-user-person">
                        <Link className="admin-user-name" to={`/users/${encodeURIComponent(user.id)}`}>{name}</Link>
                        <div className="admin-user-contact">{user.email || t('users.noEmail')}</div>
                        <div className="admin-user-muted">{user.phone || t('users.noPhone')}</div>
                        <button className="admin-user-copy-id" type="button" title={user.id} aria-label={t('users.copyId', { id: user.id })}
                          onClick={() => { void navigator.clipboard?.writeText(user.id); }}>{shortId(user.id)}</button>
                        <div className="admin-user-muted">{t('users.created', { date: formatMaybeDate(user.createdAt) })}</div>
                        <div className="admin-user-state"><StatusBadge tone={user.deletedAt ? 'red' : 'slate'}>{t(user.deletedAt ? 'users.deleted' : 'users.active')}</StatusBadge>
                          {user.anonymizedAt && <StatusBadge>{t('users.anonymized')}</StatusBadge>}
                        </div>
                        {user.deletedAt && <div className="admin-user-muted">{t('users.deletedAt', { date: formatMaybeDate(user.deletedAt) })}</div>}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="admin-user-onboarding-step">{user.onboardingPendingStep === 'COMPLETED' ? t('users.onboarding.COMPLETED') : t('users.onboarding.pending', { step: t(`users.onboarding.${user.onboardingPendingStep}`) })}</div>
                    <div className="admin-user-badges">
                      <OnboardingBadge label={t('users.badge.terms')} value={user.agbAccepted} />
                      <OnboardingBadge label={t('users.badge.bio')} value={user.hasBio} />
                      <OnboardingBadge label={t('users.badge.didit')} value={user.diditVerified} />
                      <OnboardingBadge label={t('users.badge.vehicle')} value={user.hasVehicle} />
                    </div>
                    <div className="admin-user-muted">{t(user.optionalEmailOptedOutAt ? 'users.emailOptedOut' : 'users.emailNotOptedOut')}</div>
                  </td>
                  <td>
                    <div className="admin-user-status-stack">
                      <StatusBadge tone={verified ? 'green' : 'slate'}>{t(`users.verification.${verificationKey}`)}</StatusBadge>
                      {verificationKey === 'OTHER' && <span className="admin-user-muted">{user.verificationStatus}</span>}
                      <span className="admin-user-muted">{t('users.filter.stripe')}</span>
                      <StatusBadge tone={user.stripeConnectStatus === 'READY' ? 'green' : user.stripeConnectStatus === 'PAYOUTS_DISABLED' ? 'red' : user.stripeConnectStatus === 'IN_REVIEW' ? 'amber' : 'slate'}>{t(`users.stripe.${user.stripeConnectStatus || 'NO_ACCOUNT'}`)}</StatusBadge>
                    </div>
                  </td>
                  <td>
                    <div className="admin-user-count-heading">{t('users.activity.created')}</div>
                    <dl className="admin-user-counts">
                      <div><dt>{t('users.activity.single')}</dt><dd>{user.singleRideCount}</dd></div>
                      <div><dt>{t('users.activity.recurring')}</dt><dd>{user.recurringRideCount}</dd></div>
                      <div><dt>{t('users.activity.requests')}</dt><dd>{user.rideRequestCount}</dd></div>
                    </dl>
                    <div className="admin-user-count-heading">{t('users.activity.completed')}</div>
                    <dl className="admin-user-counts">
                      <div><dt>{t('users.activity.rides')}</dt><dd>{user.completedRideCount}</dd></div>
                      <div><dt>{t('users.activity.requests')}</dt><dd>{user.completedRideRequestCount}</dd></div>
                    </dl>
                  </td>
                  <td>
                    <div className="admin-user-last-seen">{user.lastSeenAt ? formatMaybeDate(user.lastSeenAt) : t('users.activity.never')}</div>
                    <Link className="admin-user-open" aria-label={t('users.openFor', { name })} to={`/users/${encodeURIComponent(user.id)}`}>{t('users.open')}</Link>
                  </td>
                </tr>;
              })}
              {!loading && rows.length === 0 && <tr><td colSpan={5} className="admin-user-empty">{t('users.empty')}</td></tr>}
            </tbody>
          </table>
        </div>
        <PaginationBar loading={loading} pageNumber={pageNumber} pageSize={pageSize} rowCount={rows.length}
          displayPage={page ?? undefined}
          totalElements={page?.totalElements} isLast={page?.last}
          onPrevious={() => setPageNumber((number) => Math.max(0, number - 1))} onNext={() => setPageNumber((number) => number + 1)}
          previousLabel={t('users.previous')} nextLabel={t('users.next')} />
      </section>
    </PageShell>
  );
};

export default AdminUsersPage;
